import {
  paginateDtoolsPdfPackage,
  restoreDtoolsPdfPackagePagination,
} from "@/lib/dtoolsPdfPrintPagination";
import { resolveProposalPdfDocumentTitle } from "@/lib/proposalPdfFilename";

const DEFAULT_IMAGE_PRELOAD_TIMEOUT_MS = 3000;

/** Screen-only hide — avoids Tailwind `hidden` (display:none) which breaks Safari print. */
export const PROPOSAL_PDF_PACKAGE_SCREEN_HIDDEN_CLASS = "dtools-proposal-pdf-package-screen-hidden";

/** Applied after prep until afterprint — keeps package paintable for WebKit print. */
export const PROPOSAL_PDF_PACKAGE_PRINT_READY_CLASS = "dtools-proposal-pdf-package-print-ready";

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function hasValidImageSrc(img: HTMLImageElement): boolean {
  const raw = (img.getAttribute("src") ?? img.currentSrc ?? "").trim();
  if (!raw) return false;
  if (raw === "#" || raw === "about:blank") return false;
  return true;
}

function waitForSingleImage(img: HTMLImageElement): Promise<void> {
  return new Promise((resolve) => {
    if (img.complete) {
      resolve();
      return;
    }

    let settled = false;
    const done = () => {
      if (settled) return;
      settled = true;
      img.removeEventListener("load", done);
      img.removeEventListener("error", done);
      resolve();
    };

    img.addEventListener("load", done, { once: true });
    img.addEventListener("error", done, { once: true });
  });
}

/** Wait for images inside the curated PDF package before opening the print dialog. */
export async function waitForProposalPdfImages(
  root: ParentNode = document,
  timeoutMs = DEFAULT_IMAGE_PRELOAD_TIMEOUT_MS,
): Promise<void> {
  const images = [...root.querySelectorAll<HTMLImageElement>(".dtools-proposal-pdf-package img")].filter(
    hasValidImageSrc,
  );
  if (images.length === 0) return;

  for (const img of images) {
    img.loading = "eager";
  }

  await Promise.race([Promise.all(images.map(waitForSingleImage)), delay(timeoutMs)]);
}

type PdfPackageMaterializeRestore = () => void;

/**
 * Reveal the PDF package for image preload and pagination without display:none.
 * Package stays print-ready until afterprint (Safari cannot paint re-hidden DOM).
 */
export function materializeProposalPdfPackage(pkg: HTMLElement): PdfPackageMaterializeRestore {
  const previous = {
    className: pkg.className,
    style: pkg.getAttribute("style"),
  };

  pkg.classList.remove(PROPOSAL_PDF_PACKAGE_SCREEN_HIDDEN_CLASS, "hidden");
  pkg.classList.add(PROPOSAL_PDF_PACKAGE_PRINT_READY_CLASS);
  pkg.style.removeProperty("display");

  return () => {
    pkg.className = previous.className;
    if (previous.style === null) pkg.removeAttribute("style");
    else pkg.setAttribute("style", previous.style);
  };
}

let restorePagination: (() => void) | null = null;
let restoreDocumentTitle: (() => void) | null = null;
let restoreMaterialize: (() => void) | null = null;

function ensurePrintCleanupListeners(): void {
  if (typeof window === "undefined") return;
  if ((window as unknown as { __proposalPdfPrintCleanupBound?: boolean }).__proposalPdfPrintCleanupBound) return;
  (window as unknown as { __proposalPdfPrintCleanupBound?: boolean }).__proposalPdfPrintCleanupBound = true;
  window.addEventListener("afterprint", () => {
    restorePagination?.();
    restorePagination = null;
    restoreMaterialize?.();
    restoreMaterialize = null;
    restoreDocumentTitle?.();
    restoreDocumentTitle = null;
  });
}

export type PrepareProposalPdfPrintInput = {
  proposalRef?: string | null;
};

/** Materialize the hidden PDF package, paginate, preload images — leave print-ready until afterprint. */
export async function prepareProposalPdfPrint(input: PrepareProposalPdfPrintInput = {}): Promise<void> {
  const pkg = document.querySelector(".dtools-proposal-pdf-package");
  if (!(pkg instanceof HTMLElement)) return;

  ensurePrintCleanupListeners();

  const printTitle = resolveProposalPdfDocumentTitle({ proposalRef: input.proposalRef, packageRoot: document });
  if (printTitle) {
    const previousTitle = document.title;
    document.title = printTitle;
    restoreDocumentTitle = () => {
      document.title = previousTitle;
    };
  }

  restoreMaterialize = materializeProposalPdfPackage(pkg);
  try {
    restorePagination = paginateDtoolsPdfPackage(pkg);
    await waitForProposalPdfImages(pkg);
  } catch (error) {
    restoreMaterialize();
    restoreMaterialize = null;
    throw error;
  }
}

export function resetProposalPdfPrintState(): void {
  const pkg = document.querySelector(".dtools-proposal-pdf-package");
  if (pkg instanceof HTMLElement) restoreDtoolsPdfPackagePagination(pkg);
  restorePagination = null;
  restoreMaterialize?.();
  restoreMaterialize = null;
  restoreDocumentTitle?.();
  restoreDocumentTitle = null;
}
